import { useEffect, useRef, useState } from 'react';
import { Link , useLocation, useNavigate } from 'react-router-dom';
import './../../styles/Dashboard.css';
import config from '../../constants/config.js';
import Pagination from './Pagination.js';

function ProductTable({ dashboard }) {
    const location = useLocation();
    const navigate = useNavigate();

    const [ openMenuIndex, setOpenMenuIndex ] = useState( null );
    const menuRefs = useRef( [] );

    const [ tableData , setTableData ] = useState([]);
    const [ loadingTable , setLoadingTable ] = useState( true );
    const [ currentPage , setPage ] = useState( getPageInUrl() );
    const [ totalPages , setTotalPages ] = useState( 1 );
    const limit = dashboard ? 10 : 50;
    
    
    useEffect(() => {
        async function loadData() {
            setLoadingTable( true );

            try {
                const url = `${config.app.API_URL}/api/${config.app.REACT_APP_API_VERSION}/products?dashboard=${dashboard}&limit=${limit}&page=${currentPage}`;
                const requestSettings = {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        mode: getMode(),
                        single_selected_store: getSingleSelectedStore(),
                        multi_selected_store: getMultiSelectedStore()
                    })
                };
                const response = await fetch( url , requestSettings );
                const data = await response.json();
                
                if ( data?.products?.length > 0 ) {
                    setTableData( data.products );
                    setTotalPages( data?.pagination?.total_pages || 1 );
                }
            } catch ( error ) {
                console.log( error );
            } finally {
                setLoadingTable( false );
            }
        }

        loadData();
    } , [ currentPage ] );

    useEffect(() => {
        function handleClickOutside(event) {
            if ( openMenuIndex !== null && menuRefs.current[openMenuIndex] && 
                !menuRefs.current[ openMenuIndex ].contains(event.target)) {
                setOpenMenuIndex(null);
            }
        }

        if ( openMenuIndex !== null ) {
            document.addEventListener( "mousedown", handleClickOutside );
        }

        return () => {
            document.removeEventListener( "mousedown", handleClickOutside );
        };
    }, [ openMenuIndex ] );
    
    function toggleMenu( index ) {
        setOpenMenuIndex( ( prevIndex ) => ( prevIndex === index ? null : index ) );
    }

    function formatTimeAgo( timestamp ) {
        const now = new Date();
        const lastModified = new Date(timestamp);

        if ( isNaN( lastModified.getTime() ) ) {
            return "NEVER";
        }

        const diffInSeconds = Math.floor((now - lastModified) / 1000);
    
        if (diffInSeconds < 60) {
            return `${diffInSeconds} sec(s) ago`;
        } else if (diffInSeconds < 3600) {
            return `${Math.floor(diffInSeconds / 60)} min(s) ago`;
        } else if (diffInSeconds < 86400) {
            return `${Math.floor(diffInSeconds / 3600)} hour(s) ago`;
        } else if (diffInSeconds < 432000) {
            return `${Math.floor(diffInSeconds / 86400)} day(s) ago`;
        } else {
            return lastModified.toLocaleDateString();
        }
    }

    function getPageInUrl() {
        const queryParams = new URLSearchParams( location.search );
        const pageFromUrl = parseInt( queryParams.get('page') || 1 );
        return pageFromUrl;
    }

    function updatePageInUrl( newPage ) {
        const queryParams = new URLSearchParams( location.search );
        queryParams.set( 'page', newPage );
        navigate({ search: queryParams.toString() });
    }

    function goToNextPage() {
        if ( currentPage < totalPages ) {
            setPage( currentPage + 1 );
            updatePageInUrl(currentPage + 1)
        }
    }

    function goToPreviousPage() {
        if ( currentPage > 1 ) {
            setPage( currentPage - 1 );
            updatePageInUrl(currentPage - 1)
        }
    }

    function goToPage( page ) {
        if ( page < 1 || page > totalPages ) { 
            return 
        };

        setPage( page );
        updatePageInUrl( page );
    }

    function getSingleSelectedStore() {
        let store = JSON.parse( localStorage.getItem( 'single_store_selected' ) ) || null;
        return store;
    }

    function getMultiSelectedStore() {
        let stores = JSON.parse( localStorage.getItem( 'multi_stores_selected' ) ) || null;
        return stores;
    }

    function getMode() {
        let mode = localStorage.getItem( 'store_selection_mode' );
        return mode;
    }
    
    async function runAutomatedTest( productHandle , triggerIndex ) {
        console.log( 'running automated test...' );
        toggleMenu( triggerIndex );
        return;
    }

    if ( loadingTable ) {
        return(
            <div className="product-table-skeleton"></div>
        )
    }

    return(
        <div>
            <div className="dashboard-table">
                <table id="recent-products">
                    <thead>
                        <tr>
                            <th>PRODUCT</th>
                            <th>STORES</th>
                            <th>STATUS</th>
                            <th>EDITOR</th>
                            <th>LAST MODIFIED</th>
                            <th>{/* ACTIONS */}</th>
                        </tr>
                    </thead>
                    <tbody>
                        { 
                            tableData?.length > 0 ? 
                                tableData.map( ( row , index ) => {
                                    return(
                                        <tr className="product-row" key={ index }>
                                            <td>
                                                <div className="product-details">
                                                    {/* If status = PUBLISHED && has_config_errors = true, add svg */}
                                                    {  row?.status == "PUBLISHED" && row?.has_config_errors == true &&
                                                        <svg width="50" height="35" viewBox="0 0 50 35" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M25 2.5C26.9698 2.5 28.9204 2.88799 30.7403 3.64181C32.5601 4.39563 34.2137 5.50052 35.6066 6.8934C36.9995 8.28628 38.1044 9.93986 38.8582 11.7597C39.612 13.5796 40 15.5302 40 17.5C40 19.4698 39.612 21.4204 38.8582 23.2403C38.1044 25.0601 36.9995 26.7137 35.6066 28.1066C34.2137 29.4995 32.5601 30.6044 30.7403 31.3582C28.9204 32.112 26.9698 32.5 25 32.5C23.0302 32.5 21.0796 32.112 19.2597 31.3582C17.4399 30.6044 15.7863 29.4995 14.3934 28.1066C13.0005 26.7137 11.8956 25.0601 11.1418 23.2403C10.388 21.4204 10 19.4698 10 17.5C10 15.5302 10.388 13.5796 11.1418 11.7597C11.8956 9.93986 13.0005 8.28628 14.3934 6.8934C15.7863 5.50052 17.4399 4.39563 19.2597 3.64181C21.0796 2.88799 23.0302 2.5 25 2.5ZM25 35C29.6413 35 34.0925 33.1563 37.3744 29.8744C40.6563 26.5925 42.5 22.1413 42.5 17.5C42.5 12.8587 40.6563 8.40752 37.3744 5.12563C34.0925 1.84374 29.6413 0 25 0C20.3587 0 15.9075 1.84374 12.6256 5.12563C9.34374 8.40752 7.5 12.8587 7.5 17.5C7.5 22.1413 9.34374 26.5925 12.6256 29.8744C15.9075 33.1563 20.3587 35 25 35ZM26.25 8.75C26.25 8.0625 25.6875 7.5 25 7.5C24.3125 7.5 23.75 8.0625 23.75 8.75V18.75C23.75 19.4375 24.3125 20 25 20C25.6875 20 26.25 19.4375 26.25 18.75V8.75ZM25 26.875C25.4973 26.875 25.9742 26.6775 26.3258 26.3258C26.6775 25.9742 26.875 25.4973 26.875 25C26.875 24.5027 26.6775 24.0258 26.3258 23.6742C25.9742 23.3225 25.4973 23.125 25 23.125C24.5027 23.125 24.0258 23.3225 23.6742 23.6742C23.3225 24.0258 23.125 24.5027 23.125 25C23.125 25.4973 23.3225 25.9742 23.6742 26.3258C24.0258 26.6775 24.5027 26.875 25 26.875ZM6.05469 5.35937C6.42969 4.78125 6.25781 4.00781 5.67969 3.63281C5.10156 3.25781 4.32812 3.42969 3.95312 4.00781C1.44531 7.90625 0 12.5312 0 17.5C0 22.4688 1.44531 27.0938 3.94531 30.9922C4.32031 31.5703 5.09375 31.7422 5.67187 31.3672C6.25 30.9922 6.42188 30.2187 6.04688 29.6406C3.80469 26.1406 2.5 21.9766 2.5 17.5C2.5 13.0234 3.80469 8.85937 6.05469 5.35937ZM46.0547 4.00781C45.6797 3.42969 44.9062 3.25781 44.3281 3.63281C43.75 4.00781 43.5781 4.78125 43.9531 5.35937C46.1953 8.85937 47.5 13.0234 47.5 17.5C47.5 21.9766 46.1953 26.1406 43.9453 29.6406C43.5703 30.2187 43.7422 30.9922 44.3203 31.3672C44.8984 31.7422 45.6719 31.5703 46.0469 30.9922C48.5469 27.0938 50 22.4688 50 17.5C50 12.5312 48.5469 7.90625 46.0547 4.00781Z" fill="#F5812C"/>
                                                        </svg>
                                                    }

                                                    <div>
                                                        <a className="product-title" href={`/products/${row?.product_handle}`}>{ row?.product_title || "" }</a>
                                                    </div>
                                                </div>
                                                
                                            </td>
                                            <td>
                                                <div className="store-badges">
                                                    { row?.stores?.length > 0 &&
                                                        row.stores.map( ( store , i ) => {
                                                            return(
                                                                <div 
                                                                    key={ `row-${index}-store-${i}` }
                                                                    style={{ 
                                                                        backgroundColor: store?.badge_background_color || '#ffffff',
                                                                        color: store?.badge_text_color || '#000000' 
                                                                    }} 
                                                                    className="store-badge"
                                                                >
                                                                    { store?.store_nickname || "" }
                                                                </div>
                                                            )
                                                        })
                                                    }
                                                </div>
                                            </td>
                                            <td>
                                                <div className="status normal">
                                                    { row?.status || "" }
                                                </div>
                                            </td>
                                            <td>
                                                <div className="editor normal">
                                                    { row?.editor || "" }
                                                </div>
                                            </td>
                                            <td>
                                                <div className="last-editor normal">
                                                    { formatTimeAgo( row?.last_modified ) }
                                                </div>
                                            </td>
                                            <td>
                                                <div className="actions" ref={(el) => menuRefs.current[index] = el}>
                                                    <button className="more-options" onClick={() => toggleMenu( index )}>
                                                        ...
                                                    </button>

                                                    { openMenuIndex === index && (
                                                        <ul className="options-menu">
                                                            { row?.actions?.length > 0 &&
                                                                row.actions.map( ( action , i ) => {
                                                                    return(
                                                                        <li 
                                                                            className="option-menu-li"
                                                                            key={ `row-${index}-action-${i}`
                                                                        }>
                                                                            { action?.action_type == 'LINK' &&
                                                                                <Link className="option-action" to={action?.url || '/'} target="_blank" rel="noopener noreferrer">
                                                                                    { action?.title }
                                                                                </Link>
                                                                            }

                                                                            { action?.action_type == 'CALLBACK' && action?.callback_function_to_run == "AUTOMATED_TEST" &&
                                                                                <button className="option-action" onClick={() => {
                                                                                    runAutomatedTest( row?.product_handle , index )
                                                                                }}>
                                                                                    { action?.title }
                                                                                </button>
                                                                            }
                                                                        </li>
                                                                    )
                                                                })
                                                            }
                                                        </ul>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                })
                            :
                            <tr>
                                <td colSpan="6">No Products Found</td>
                            </tr>
                        }
                    </tbody>
                </table>
                
                { dashboard && 
                    <div className="view-all">
                        <Link className="view-link" to="/products">
                            VIEW ALL PRODUCTS
                        </Link>
                    </div>  
                }
                
                { !dashboard && (
                    <Pagination 
                        currentPage={currentPage} 
                        totalPages={totalPages} 
                        goToPage={goToPage}
                        visible={5}
                    />
                )}
            </div>
        </div>
    );
}

export default ProductTable;